import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import type { EventData, ParentMomentData } from '../services/momentsApi';
import type { MilestoneAttributesData } from '../services/milestoneAttributesService';
import { globalCacheSaga } from './globalCacheSaga';
import type { RootState } from 'types';

export interface GlobalCacheState {
  events: {
    data: EventData[];
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
    // Cache for 5 minutes (300000ms)
    cacheExpiry: number;
  };
  parentMoments: {
    data: ParentMomentData[];
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
    // Cache for 5 minutes (300000ms)
    cacheExpiry: number;
  };
  milestoneAttributes: {
    data: MilestoneAttributesData | null;
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
    // Cache for 1 hour (3600000ms) - milestone attributes are more static
    cacheExpiry: number;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const MILESTONE_ATTRIBUTES_CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export const initialState: GlobalCacheState = {
  events: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    cacheExpiry: CACHE_DURATION
  },
  parentMoments: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    cacheExpiry: CACHE_DURATION
  },
  milestoneAttributes: {
    data: null,
    loading: false,
    error: null,
    lastUpdated: null,
    cacheExpiry: MILESTONE_ATTRIBUTES_CACHE_DURATION
  }
};

const globalCacheSlice = createSlice({
  name: 'globalCache',
  initialState,
  reducers: {
    // Events actions
    loadEvents(state) {
      state.events.loading = true;
      state.events.error = null;
    },
    eventsLoaded(state, action: PayloadAction<EventData[]>) {
      state.events.data = action.payload;
      state.events.loading = false;
      state.events.error = null;
      state.events.lastUpdated = Date.now();
    },
    eventsLoadError(state, action: PayloadAction<string>) {
      state.events.error = action.payload;
      state.events.loading = false;
    },

    // Parent moments actions
    loadParentMoments(state) {
      state.parentMoments.loading = true;
      state.parentMoments.error = null;
    },
    // ✅ NEW: Force refresh parent moments (bypasses cache check)
    forceRefreshParentMoments(state) {
      state.parentMoments.loading = true;
      state.parentMoments.error = null;
    },
    parentMomentsLoaded(state, action: PayloadAction<ParentMomentData[]>) {
      state.parentMoments.data = action.payload;
      state.parentMoments.loading = false;
      state.parentMoments.error = null;
      state.parentMoments.lastUpdated = Date.now();
    },
    parentMomentsLoadError(state, action: PayloadAction<string>) {
      state.parentMoments.error = action.payload;
      state.parentMoments.loading = false;
    },

    // Milestone attributes actions
    loadMilestoneAttributes(state) {
      state.milestoneAttributes.loading = true;
      state.milestoneAttributes.error = null;
    },
    milestoneAttributesLoaded(
      state,
      action: PayloadAction<MilestoneAttributesData>
    ) {
      state.milestoneAttributes.data = action.payload;
      state.milestoneAttributes.loading = false;
      state.milestoneAttributes.error = null;
      state.milestoneAttributes.lastUpdated = Date.now();
    },
    milestoneAttributesLoadError(state, action: PayloadAction<string>) {
      state.milestoneAttributes.error = action.payload;
      state.milestoneAttributes.loading = false;
    },

    // Combined action to load all data at app initialization
    loadGlobalCacheData(state) {
      // Only set loading if data is stale or missing
      const now = Date.now();
      const eventsStale =
        !state.events.lastUpdated ||
        now - state.events.lastUpdated > state.events.cacheExpiry;
      const parentMomentsStale =
        !state.parentMoments.lastUpdated ||
        now - state.parentMoments.lastUpdated > state.parentMoments.cacheExpiry;
      const milestoneAttributesStale =
        !state.milestoneAttributes.lastUpdated ||
        now - state.milestoneAttributes.lastUpdated >
          state.milestoneAttributes.cacheExpiry;

      if (eventsStale) {
        state.events.loading = true;
        state.events.error = null;
      }
      if (parentMomentsStale) {
        state.parentMoments.loading = true;
        state.parentMoments.error = null;
      }
      if (milestoneAttributesStale) {
        state.milestoneAttributes.loading = true;
        state.milestoneAttributes.error = null;
      }
    },

    // Clear cache (for testing or forced refresh)
    clearEventsCache(state) {
      state.events = {
        ...initialState.events
      };
    },
    clearParentMomentsCache(state) {
      state.parentMoments = {
        ...initialState.parentMoments
      };
    },
    clearMilestoneAttributesCache(state) {
      state.milestoneAttributes = {
        ...initialState.milestoneAttributes
      };
    },
    clearAllCache(state) {
      return initialState;
    },

    // Update cache expiry settings
    setCacheExpiry(
      state,
      action: PayloadAction<{ events?: number; parentMoments?: number }>
    ) {
      const { events, parentMoments } = action.payload;
      if (events !== undefined) {
        state.events.cacheExpiry = events;
      }
      if (parentMoments !== undefined) {
        state.parentMoments.cacheExpiry = parentMoments;
      }
    }
  }
});

export const { actions: globalCacheActions, reducer: globalCacheReducer } =
  globalCacheSlice;

// Selectors
export const selectEvents = (state: RootState) => state.globalCache?.events;
export const selectParentMoments = (state: RootState) =>
  state.globalCache?.parentMoments;
export const selectMilestoneAttributes = (state: RootState) =>
  state.globalCache?.milestoneAttributes;

// Helper selectors
export const selectEventsData = (state: RootState) =>
  state.globalCache?.events?.data || [];
export const selectParentMomentsData = (state: RootState) =>
  state.globalCache?.parentMoments?.data || [];
export const selectMilestoneAttributesData = (state: RootState) =>
  state.globalCache?.milestoneAttributes?.data || null;

export const selectIsEventsLoading = (state: RootState) =>
  state.globalCache?.events?.loading || false;
export const selectIsParentMomentsLoading = (state: RootState) =>
  state.globalCache?.parentMoments?.loading || false;
export const selectIsMilestoneAttributesLoading = (state: RootState) =>
  state.globalCache?.milestoneAttributes?.loading || false;

export const selectEventsError = (state: RootState) =>
  state.globalCache?.events?.error || null;
export const selectParentMomentsError = (state: RootState) =>
  state.globalCache?.parentMoments?.error || null;
export const selectMilestoneAttributesError = (state: RootState) =>
  state.globalCache?.milestoneAttributes?.error || null;

// Cache validation selectors
export const selectIsEventsCacheValid = (state: RootState) => {
  const events = state.globalCache?.events;
  if (!events?.lastUpdated) return false;
  return Date.now() - events.lastUpdated < events.cacheExpiry;
};

export const selectIsParentMomentsCacheValid = (state: RootState) => {
  const parentMoments = state.globalCache?.parentMoments;
  if (!parentMoments?.lastUpdated) return false;
  return Date.now() - parentMoments.lastUpdated < parentMoments.cacheExpiry;
};

// Hook for using the global cache slice
export const useGlobalCacheSlice = () => {
  useInjectReducer({ key: 'globalCache', reducer: globalCacheReducer });
  useInjectSaga({ key: 'globalCache', saga: globalCacheSaga });
  return { actions: globalCacheActions };
};
